import { Ref } from 'vue';
import { defineStore } from 'pinia';
import { AsyncState } from '@borg/types';

interface UserElementsStoreSetup<T, D> {
  elementName: string;
  manager: ReturnType<typeof userElementsManager<T, D>>;
}

export function userElementsStoreFactory<T extends { id: number | string }, D = Partial<Omit<T, 'id'>>>(
  setup: UserElementsStoreSetup<T, D>,
) {
  return defineStore(setup.elementName, () => {
    const items = ref<T[]>([]) as Ref<T[]>;
    const getManyState = ref<AsyncState>('isInitial');
    const setManyState = ref<AsyncState>('isInitial');
    const getOneState = ref<AsyncState>('isInitial');
    const createState = ref<AsyncState>('isInitial');
    const updateState = ref<AsyncState>('isInitial');
    const removeState = ref<AsyncState>('isInitial');

    function setItems(data: T[]) {
      items.value = data;
    }

    async function getMany() {
      try {
        getManyState.value = 'inProgress';
        const result = await setup.manager.getMany();
        if (result.isSuccess) {
          getManyState.value = 'isSuccess';
          items.value = result.data;
        } else {
          getManyState.value = 'isInvalid';
        }
      } catch (error) {
        getManyState.value = 'hasError';
        console.error(`Oops! Error in getMany() ${setup.elementName}`);
      }
    }

    async function setMany(...args: Parameters<typeof setup.manager.setMany>) {
      try {
        setManyState.value = 'inProgress';
        const result = await setup.manager.setMany(...args);
        if (result.isSuccess) {
          setManyState.value = 'isSuccess';
          items.value = result.data;
        } else {
          setManyState.value = 'isInvalid';
        }
      } catch (error) {
        setManyState.value = 'hasError';
      }
    }

    async function getOne(...args: Parameters<typeof setup.manager.getOne>) {
      // todo - what with this?? feedback not implemented
      try {
        getOneState.value = 'inProgress';
        const result = await setup.manager.getOne(...args);
        if (result.isSuccess) {
          getOneState.value = 'isSuccess';
          // items.value = result.data;
        } else {
          getOneState.value = 'isInvalid';
        }
      } catch (error) {
        getOneState.value = 'hasError';
      }
    }

    async function create(...args: Parameters<typeof setup.manager.create>) {
      try {
        createState.value = 'inProgress';
        const result = await setup.manager.create(...args);
        if (result.isSuccess) {
          createState.value = 'isSuccess';
          items.value = [...items.value, result.data];
        } else {
          createState.value = 'isInvalid';
        }
      } catch (error) {
        createState.value = 'hasError';
      }
    }

    async function update(...args: Parameters<typeof setup.manager.update>) {
      try {
        updateState.value = 'inProgress';
        const result = await setup.manager.update(...args);
        if (result.isSuccess) {
          updateState.value = 'isSuccess';
          const index = items.value.findIndex((v) => v.id === args[0]);
          if (index !== -1) {
            items.value = [...items.value.slice(0, index), result.data, ...items.value.slice(index + 1)];
          } else {
            items.value = [...items.value, result.data];
          }
        } else {
          updateState.value = 'isInvalid';
        }
      } catch (error) {
        updateState.value = 'hasError';
      }
    }

    async function remove(...args: Parameters<typeof setup.manager.delete>) {
      try {
        removeState.value = 'inProgress';
        const result = await setup.manager.delete(...args);
        if (result.isSuccess) {
          removeState.value = 'isSuccess';
          items.value = items.value.filter((i) => i.id !== args[0]);
        } else {
          removeState.value = 'isInvalid';
        }
      } catch (error) {
        removeState.value = 'hasError';
      }
    }

    return {
      items,
      setItems,
      getMany,
      getManyState,
      setMany,
      setManyState,
      getOne,
      getOneState,
      create,
      createState,
      update,
      updateState,
      remove,
      removeState,
    };
  });
}
